import { Helmet } from 'react-helmet';
import logo from '../logo.svg';

// Main Component for the Palm Key Product Page
const PalmKeyProductPage = () => {

    // The CSS is embedded here for convenience and to keep the component self-contained.
    // In a larger application, you would typically move this to a separate CSS file.
    const styles = `
      .palm-key-page {
        font-family: 'Inter', sans-serif;
        background-color: #0c0c0c;
        color: #F4F4F4;
        position: fixed; /* Changed to fixed to prevent scrolling the page itself */
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow: hidden; /* Prevents all scrolling */
        display: flex;
        align-items: center;
        justify-content: center;
      }
   
      .palm-key-background-animation {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        overflow: hidden;
      }
   
      .palm-key-circle {
        position: absolute;
        border-radius: 50%;
        filter: blur(140px);
        opacity: 0.6;
      }
   
      .pk-c1 {
        width: 500px; height: 500px;
        background: #eb1918; /* Main Brand Red */
        top: 10%; left: 15%;
        animation: palmKeyMove1 22s ease-in-out alternate infinite;
      }
   
      .pk-c2 {
        width: 600px; height: 600px;
        background: #eb1918; /* Main Brand Red */
        bottom: 10%; right: 15%;
        animation: palmKeyMove2 28s ease-in-out alternate-reverse infinite;
      }
       
      .pk-c3 {
        width: 400px; height: 400px;
        background: #eb1918; /* Main Brand Red */
        top: 40%; right: 20%;
        animation: palmKeyMove3 25s ease-in-out alternate infinite;
      }
   
      @keyframes palmKeyMove1 {
        from { transform: translate(0, 0) scale(1) rotate(0deg); }
        to { transform: translate(-10vw, 5vh) scale(1.1) rotate(-30deg); }
      }
      @keyframes palmKeyMove2 {
        from { transform: translate(0, 0) scale(1.1) rotate(0deg); }
        to { transform: translate(5vw, -10vh) scale(1) rotate(40deg); }
      }
      @keyframes palmKeyMove3 {
          from { transform: translate(0, 0) scale(1); }
          to { transform: translate(10vw, 10vh) scale(1.2); }
      }
   
      .palm-key-wrapper {
        position: relative;
        z-index: 2;
        width: 100%;
        max-width: 1400px;
        padding: 1rem 2rem; /* Added vertical padding here for breathing room */
        display: flex;
        flex-direction: column;
        gap: 1.5rem; /* Reduced gap */
        margin: 0 auto;
        max-height: 100vh;
        overflow-y: auto; /* Allow internal scrolling only if needed on very small heights */
        box-sizing: border-box;
      }
   
      .palm-key-glass-box {
        background: rgba(30, 30, 35, 0.55);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        border-radius: 24px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        padding: 2rem; /* Reduced padding */
        box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.4);
      }
       
      .palm-key-hero {
        text-align: center;
      }
   
      .palm-key-hero h1 {
          font-size: 3rem; /* Reduced font size */
          font-weight: 700;
          background: -webkit-linear-gradient(45deg, #eb1918, #FFD166);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 0.75rem; /* Reduced margin */
      }
   
      .palm-key-hero p {
          font-size: 1.1rem; /* Reduced font size */
          max-width: 700px;
          margin: 0 auto;
          opacity: 0.9;
      }
   
      .palm-key-features-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.5rem; /* Reduced gap */
      }
       
      .palm-key-feature-box {
        text-align: left;
        display: flex;
        flex-direction: column;
        background: rgba(45, 45, 50, 0.6);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        padding: 1.5rem; /* Specifically reduced padding for feature boxes */
      }
       
      .palm-key-feature-box:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px 0 rgba(0, 0, 0, 0.5);
      }
       
      .palm-key-feature-box .icon {
          margin-bottom: 1rem; /* Reduced margin */
          width: 40px; /* Reduced icon size */
          height: 40px; /* Reduced icon size */
      }
       
      .palm-key-feature-box h3 {
          font-size: 1.25rem; /* Reduced font size */
          margin-bottom: 0.5rem; /* Reduced margin */
          color: #fff;
      }

      .palm-key-feature-box p {
          font-size: 0.9rem; /* Reduced font size */
          line-height: 1.4;
      }

      /* Floating Home Button */
      .floating-home-btn {
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        z-index: 1000;
        background-color: rgba(235, 25, 24, 0.8);
        color: white;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 20px rgba(0,0,0,0.4);
        border: 1px solid rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(5px);
        -webkit-backdrop-filter: blur(5px);
        transition: transform 0.3s ease, background-color 0.3s ease;
      }

      .floating-home-btn:hover {
        transform: scale(1.1);
        background-color: rgba(235, 25, 24, 1);
      }

      .floating-home-btn svg {
        width: 28px;
        height: 28px;
      }
   
      /* Responsive adjustments */
      @media (max-width: 768px) {
          .palm-key-page {
            /* On smaller screens, allow scrolling as content will definitely overflow */
            position: relative; /* Revert to relative for normal document flow */
            height: auto;
          }
          .palm-key-wrapper {
            padding: 1.5rem;
            gap: 2rem;
            overflow-y: visible; /* Remove internal scroll for mobile */
            max-height: none;
          }
          .palm-key-hero h1 {
              font-size: 2.5rem;
          }
          .palm-key-hero p {
              font-size: 1.1rem;
          }
          .floating-home-btn {
            width: 50px;
            height: 50px;
            bottom: 1rem;
            right: 1rem;
          }
      }
    `;
   
    return (
      <>
        <Helmet>
            <meta charSet="utf-8" />
            <title>Palm Key™ - PalmDigitalz</title>
            <link rel="canonical" href="http://palmdigitalz.com/palm-key" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <meta name="description" content="Palm Key: The Official Insights Extraction Engine of PalmDigitalz" />
            <meta name="keywords" content="palm key, Palm Key, mainframe modernization, app modernization,COBOL code insights extraction, meta data extraction, legacy app meta data extraction" />
            <link rel="icon" href={logo}></link>
        </Helmet>

        <style>{styles}</style>
        <div className="palm-key-page">
          {/* SVG Gradient for Icons */}
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <linearGradient id="palm-key-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#eb1918' }} />
                <stop offset="100%" style={{ stopColor: '#FFD166' }} />
              </linearGradient>
            </defs>
          </svg>
   
          {/* Animated Background */}
          <div className="palm-key-background-animation">
            <div className="palm-key-circle pk-c1"></div>
            <div className="palm-key-circle pk-c2"></div>
            <div className="palm-key-circle pk-c3"></div>
          </div>
   
          {/* Page Content */}
          <div className="palm-key-wrapper">
            <header className="palm-key-hero palm-key-glass-box">
              <h1>Palm Key™</h1>
              <p>The definitive pre-processing engine for legacy systems. Palm Key automates the analysis of your codebase to generate crucial insights, accelerating your modernization journey with unparalleled clarity and precision.</p>
            </header>
   
            <main>
              <div className="palm-key-features-grid">
                 
                <div className="palm-key-feature-box palm-key-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-key-icon-gradient)">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>Code Pre-Processing</h3>
                  <p>Automated analysis and structuring of your entire legacy codebase, preparing it for efficient migration and modernization.</p>
                </div>
   
                <div className="palm-key-feature-box palm-key-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-key-icon-gradient)">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="15 3 21 3 21 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="10" y1="14" x2="21" y2="3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>Cross-References</h3>
                  <p>Generates detailed reports mapping all dependencies and connections between programs, files, and variables.</p>
                </div>
   
                <div className="palm-key-feature-box palm-key-glass-box">
                   <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-key-icon-gradient)">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>Business Glossary</h3>
                  <p>Extracts and defines key business terms and logic from your code, creating an invaluable knowledge base.</p>
                </div>
                 
                <div className="palm-key-feature-box palm-key-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-key-icon-gradient)">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17l10 5 10-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12l10 5 10-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>COBOL Metadata</h3>
                  <p>Provides deep insights into your COBOL copybooks, data structures, and program specifications.</p>
                </div>
   
                <div className="palm-key-feature-box palm-key-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-key-icon-gradient)">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="14 2 14 8 20 8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="16" y1="13" x2="8" y2="13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="16" y1="17" x2="8" y2="17" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="10 9 9 9 8 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <h3>CRUD Reports</h3>
                  <p>Automatically generates detailed Create, Read, Update, and Delete reports for all data entities within the system.</p>
                </div>
                 
                <div className="palm-key-feature-box palm-key-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-key-icon-gradient)">
                    <circle cx="12" cy="12" r="1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="19" cy="12" r="1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="5" cy="12" r="1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <h3>And More</h3>
                  <p>From data flow diagrams to complexity analysis, Palm Key delivers a complete suite of tools for total system understanding.</p>
                </div>
   
              </div>
            </main>
          </div>
          
          <a href="/" className="floating-home-btn" aria-label="Go to Homepage">
             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
             </svg>
          </a>

        </div>
      </>
    );
  };
   
  export default PalmKeyProductPage;
